import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { loginDiwali, startDiwali, submitDiwaliStage } from "./diwaliSlice";
import DiwaliHeader from "./DiwaliHeader";
import { botBet, botDiscards, compareHands, evaluate, newDeck } from "./pokerLogic";
import "./diwali.css";
import "./poker.css";

const POKER_BASE = "/diwaliEscapeRoom/poker/images";
const START_BANK = 500;
const DEALS = 3;
const ANTE = 10;
const UNIT = 20; // size of a raise
const MAX_RAISES = 2;
const WIN_POINTS = 10;
const BOT_NAMES = ["Rohit", "Mihir", "Priyanka"];
const BOLDNESS = [0.2, 0.5, 0.8];
const DEAL_MS = 1800; // time the dealing animation needs before betting starts

const RULES = [
  "Objective: Achieve the best hand or make the others fold.",
  "Hand Rankings: From highest to lowest - Straight Flush, Four of a Kind, Full House, Flush, Straight, Three of a Kind, Two Pair, One Pair, High Card.",
  "Betting: Options include Call or Check (match the bet), Raise (increase the bet), or Fold (withdraw from the round).",
  "Gameplay: Each player receives 5 cards and pays a 10 coin ante, bets, can discard up to 3 cards, draws new ones, and then bets again. The best hand wins the pot.",
  "You are competing against 3 other players in this game.",
  "You start with 500 coins and have 3 deals to increase your total.",
  "You earn 10 points if you have more than 500 coins after 3 deals.",
];

const FACE_NAME = { 11: "jack", 12: "queen", 13: "king", 14: "ace" };
const SUIT_NAME = { S: "spades", H: "hearts", D: "diamonds", C: "clubs" };
const faceFile = (card) =>
  `${POKER_BASE}/${FACE_NAME[card.rank] || card.rank}_of_${SUIT_NAME[card.suit]}.png`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const copy = (value) => JSON.parse(JSON.stringify(value));

function Card({ card, hidden, selected, onClick, delay }) {
  if (!card) return <div className="pcard pcard-empty" />;
  return (
    <div
      className={`pcard${hidden ? " pcard-back" : ""}${selected ? " selected" : ""}${
        onClick ? " pickable" : ""
      }`}
      style={{ animationDelay: `${delay}s` }}
      onClick={onClick}
    >
      {!hidden && <img src={faceFile(card)} alt="" draggable="false" />}
    </div>
  );
}

function Seat({ player, className, deal, dealing, onPick, picked }) {
  const faceUp = player.isHuman || player.showHand;
  const dead = player.folded || player.out;
  return (
    <div className={`seat-box ${className}`}>
      <div className="seat-cards">
        {player.hand.map((card, i) => (
          <Card
            // a new card (first deal or a draw) mounts again and plays the dealing animation
            key={`${deal}-${i}-${card.rank}${card.suit}`}
            card={card}
            hidden={!faceUp}
            selected={picked?.includes(i)}
            onClick={onPick ? () => onPick(i) : undefined}
            delay={dealing ? (i * 4 + player.id) * 0.08 : player.id * 0.05}
          />
        ))}
      </div>
      <div className={`seat-name${dead ? " dead" : ""}`}>
        <strong className="gold-text">{player.name}</strong>
        <span>Coins: {player.bank}</span>
      </div>
      <div className={`seat-bet${dead ? " folded" : ""}`}>
        {dead
          ? player.out
            ? "OUT"
            : `FOLDED (${player.roundBet})`
          : `Bet: ${player.roundBet}${player.note ? ` · ${player.note}` : ""}`}
      </div>
    </div>
  );
}

/**
 * Poker: five-card draw against three computer players. Three deals, 500 coins
 * each; finish above 500 to earn the poker points. The game is written as one
 * async "director" that waits for the player's choices.
 */
export default function DiwaliPoker() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    id: gameId,
    status: gameStatus,
    s1,
    s2,
    s3,
    s4,
    completed,
    points: savedPoints,
  } = useSelector((state) => state.diwali);
  const stagesDone = Boolean(s1 && s2 && s3 && s4);

  const [started, setStarted] = useState(false);
  const [view, setView] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [picked, setPicked] = useState([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [isPortrait, setIsPortrait] = useState(() => window.innerHeight > window.innerWidth);
  const resolver = useRef(null);
  const alive = useRef(true);
  const startedRef = useRef(false);
  const startDiwaliAttemptedRef = useRef(false);
  const pointsRef = useRef(0);
  pointsRef.current = savedPoints;

  useEffect(() => {
    const check = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // A direct visit/reload has no session in memory, and saving the final
  // score needs state.diwali.id - restore it first.
  useEffect(() => {
    if (!user) dispatch(loginDiwali());
  }, [user, dispatch]);

  // One attempt per mount; submitDiwaliStage has its own one-shot retry (see diwaliSlice.js).
  useEffect(() => {
    if (user && !gameId && !startDiwaliAttemptedRef.current) {
      startDiwaliAttemptedRef.current = true;
      dispatch(startDiwali());
    }
  }, [user, gameId, dispatch]);

  // Poker is only reachable after the four room puzzles. Wait until the saved
  // progress has loaded so a cold reload doesn't bounce a player who finished
  // them. The backend enforces the same rule on the final submit.
  useEffect(() => {
    if (gameStatus !== "succeeded") return;
    if (completed) navigate("/diwali/thankyou", { replace: true });
    else if (!stagesDone) navigate("/diwali/game", { replace: true });
  }, [gameStatus, completed, stagesDone, navigate]);

  // Round 5 always finishes the play-through. A failed save offers Retry
  // instead of leaving a finished game stuck.
  const sendFinalScore = useCallback(
    (points) => {
      dispatch(submitDiwaliStage({ points, round: 5 }))
        .unwrap()
        .then(() => navigate("/diwali/thankyou"))
        .catch((err) => {
          Swal.fire({
            title: String(err || "Failed to save progress"),
            icon: "error",
            showCancelButton: true,
            confirmButtonText: "Retry",
            cancelButtonText: "Close",
          }).then((r) => {
            if (r.isConfirmed) sendFinalScore(points);
          });
        });
    },
    [dispatch, navigate]
  );
  const sendRef = useRef(sendFinalScore);
  sendRef.current = sendFinalScore;

  const ask = useCallback(
    (next) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setPrompt(next);
      }),
    []
  );

  const answer = (value) => {
    const resolve = resolver.current;
    resolver.current = null;
    setPrompt(null);
    setPicked([]);
    if (resolve) resolve(value);
  };

  const run = useCallback(async () => {
    const g = {
      deal: 0,
      pot: 0,
      message: "",
      currentBet: 0,
      dealing: false,
      players: ["You", ...BOT_NAMES].map((name, id) => ({
        id,
        name,
        isHuman: id === 0,
        bank: START_BANK,
        hand: [],
        folded: false,
        out: false,
        roundBet: 0,
        showHand: false,
        note: "",
      })),
    };
    let currentBet = 0;
    const publish = () => {
      g.currentBet = currentBet;
      if (alive.current) setView(copy(g));
    };
    const live = () => g.players.filter((p) => !p.folded);
    const pay = (p, amount) => {
      const sum = Math.min(amount, p.bank);
      p.bank -= sum;
      p.roundBet += sum;
      g.pot += sum;
    };

    async function bettingRound(label) {
      g.message = label;
      currentBet = 0;
      g.players.forEach((p) => {
        p.roundBet = 0;
        p.note = "";
      });
      let raises = 0;
      let acted = new Set();
      let i = g.deal;
      publish();
      for (let guard = 0; guard < 200 && alive.current; guard++) {
        if (live().length <= 1) return;
        const pending = new Set(
          live()
            // eslint-disable-next-line no-loop-func
            .filter((p) => p.bank > 0 && (!acted.has(p.id) || p.roundBet < currentBet))
            .map((p) => p.id)
        );
        if (!pending.size) return;
        const p = g.players[i % g.players.length];
        i++;
        if (!pending.has(p.id)) continue;

        const toCall = Math.min(currentBet - p.roundBet, p.bank);
        const canRaise = raises < MAX_RAISES && p.bank >= toCall + UNIT;
        let action;
        if (p.isHuman) action = await ask({ type: "bet", toCall, canRaise });
        else {
          await sleep(750);
          action = botBet(p.hand, { toCall, canRaise, bold: BOLDNESS[p.id - 1] });
        }
        if (!alive.current) return;

        if (action === "fold") {
          p.folded = true;
          p.note = "Folded";
        } else if (action === "raise") {
          pay(p, toCall + UNIT);
          currentBet += UNIT;
          raises++;
          acted = new Set([p.id]);
          p.note = "Raised";
        } else {
          pay(p, toCall);
          acted.add(p.id);
          p.note = toCall ? "Called" : "Checked";
        }
        publish();
      }
    }

    async function drawRound() {
      g.message = "Draw: swap up to 3 cards";
      g.players.forEach((p) => (p.note = ""));
      publish();
      for (const p of live()) {
        let drop;
        if (p.isHuman) drop = await ask({ type: "draw" });
        else {
          await sleep(600);
          drop = botDiscards(p.hand);
        }
        if (!alive.current) return;
        // eslint-disable-next-line no-loop-func
        drop.slice(0, 3).forEach((index) => {
          p.hand[index] = deck.pop();
        });
        p.note = drop.length ? `Drew ${drop.length}` : "Stood pat";
        publish();
      }
    }

    let deck = [];
    while (alive.current) {
      g.deal += 1;
      deck = newDeck();
      g.pot = 0;
      g.message = "Dealing...";
      // eslint-disable-next-line no-loop-func
      g.players.forEach((p) => {
        p.out = p.bank <= 0;
        p.folded = p.out;
        p.showHand = false;
        p.note = "";
        p.roundBet = 0;
        p.hand = p.out ? [] : Array.from({ length: 5 }, () => deck.pop());
      });
      g.players.filter((p) => !p.out).forEach((p) => pay(p, ANTE));
      g.players.forEach((p) => (p.roundBet = 0));
      g.dealing = true;
      publish();
      await sleep(DEAL_MS);
      if (!alive.current) return;
      g.dealing = false;
      publish();

      await bettingRound("Place your bets");
      if (live().length > 1) {
        await drawRound();
        if (live().length > 1) await bettingRound("Final betting");
      }
      if (!alive.current) return;

      // showdown
      live().forEach((p) => (p.showHand = true));
      const scored = live().map((p) => ({ p, score: evaluate(p.hand) }));
      let best = scored[0];
      scored.forEach((s) => {
        if (compareHands(s.score, best.score) > 0) best = s;
      });
      const winners = scored.filter((s) => compareHands(s.score, best.score) === 0);
      const share = Math.floor(g.pot / winners.length);
      winners.forEach((w, n) => {
        w.p.bank += share + (n === 0 ? g.pot - share * winners.length : 0);
        w.p.note = `Won ${share}`;
      });
      g.message =
        scored.length === 1
          ? `${winners[0].p.name} wins ${g.pot} - everyone else folded`
          : `${winners.map((w) => w.p.name).join(" & ")} win${winners.length > 1 ? "" : "s"} ${
              g.pot
            } with ${best.score.name}`;
      g.pot = 0;
      publish();

      const human = g.players[0];
      const botsLeft = g.players.slice(1).some((p) => p.bank > 0);
      const over = g.deal >= DEALS || human.bank <= 0 || !botsLeft;
      await ask({ type: "next", last: over });
      if (!alive.current) return;
      if (over) {
        const won = human.bank > START_BANK;
        const message =
          human.bank <= 0
            ? `You Lost ${START_BANK} coins in ${g.deal} deals.`
            : won
              ? `You Won ${human.bank - START_BANK} coins in ${g.deal} deals.`
              : human.bank === START_BANK
                ? `You broke even in ${g.deal} deals.`
                : `You Lost ${START_BANK - human.bank} coins in ${g.deal} deals.`;
        await Swal.fire(message, "", won ? "success" : "info");
        if (!alive.current) return;
        // Saved score (frame stage) + the hidden-door bonus that is only ever
        // added client-side, plus the poker win bonus.
        sendRef.current((Number(pointsRef.current) || 5) + 2 + (won ? WIN_POINTS : 0));
        return;
      }
    }
  }, [ask]);

  const begin = () => {
    if (startedRef.current) return;
    if (gameStatus !== "succeeded" || !stagesDone) return;
    startedRef.current = true;
    setStarted(true);
    if (!sessionStorage.getItem("diwaliPokerRulesShown")) {
      setRulesOpen(true);
      sessionStorage.setItem("diwaliPokerRulesShown", "true");
    }
    run();
  };

  const human = view?.players[0];
  const toggle = (index) =>
    setPicked((now) =>
      now.includes(index) ? now.filter((i) => i !== index) : now.length < 3 ? [...now, index] : now
    );

  const seatClass = ["seat-bottom", "seat-left", "seat-top", "seat-right"];
  const drawing = prompt?.type === "draw";
  const ready = gameStatus === "succeeded" && stagesDone;

  return (
    <div
      className="dw-poker-root poker-page"
      style={{
        backgroundImage: `url(/diwaliEscapeRoom/images/${
          isPortrait ? "thankyou_main.jpg" : "thankyou.jpg"
        })`,
      }}
    >
      <DiwaliHeader />
      <div className="poker-top">
        <img
          src={`${POKER_BASE}/Rules.png`}
          alt="Rules"
          className="poker-rules"
          onClick={() => setRulesOpen(true)}
        />
      </div>

      <div className="poker-main">
        <div className="poker-table" style={{ backgroundImage: `url(${POKER_BASE}/poker_table.png)` }}>
          {started && view && (
            <>
              {view.players.map((p, i) => (
                <Seat
                  key={p.id}
                  player={p}
                  className={seatClass[i]}
                  deal={view.deal}
                  dealing={view.dealing}
                  picked={i === 0 && drawing ? picked : undefined}
                  onPick={i === 0 && drawing ? toggle : undefined}
                />
              ))}
              <div className="poker-center">
                <div className="poker-plaque deals-plaque">
                  Deals {view.deal}/{DEALS}
                </div>
                <div className="poker-message gold-text">{view.message}</div>
                {view.dealing && <div className="pcard pcard-back poker-deck" />}
                <div className="poker-plaque pot">Total pot: {view.pot}</div>
                {human && !human.folded && human.hand.length === 5 && !view.dealing && (
                  <div className="poker-hand-name">Your hand: {evaluate(human.hand).name}</div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="poker-controls">
          {!started && (
            <img
              src={`${POKER_BASE}/Deal.png`}
              alt="Ready to Deal"
              className="poker-deal-btn"
              style={{ opacity: ready ? 1 : 0.5 }}
              onClick={begin}
            />
          )}

          {started && view && prompt?.type === "bet" && (
            <>
              <div className="poker-info">
                <div className="poker-plaque">
                  Current raise: <b>{view.currentBet}</b>
                </div>
                <div className="poker-plaque">
                  {prompt.toCall ? (
                    <>
                      You need <b>{prompt.toCall}</b> more to call
                    </>
                  ) : (
                    "Nothing to call"
                  )}
                </div>
              </div>
              <div className="poker-actions">
                <button type="button" className="poker-btn fold" onClick={() => answer("fold")}>
                  Fold
                </button>
                <button type="button" className="poker-btn call" onClick={() => answer("call")}>
                  {prompt.toCall ? `CALL ${prompt.toCall}` : "CHECK"}
                </button>
                {prompt.canRaise ? (
                  <button type="button" className="poker-btn raise" onClick={() => answer("raise")}>
                    Raise +{UNIT}
                  </button>
                ) : (
                  <span className="poker-btn spacer" />
                )}
              </div>
            </>
          )}

          {started && view && drawing && (
            <div className="poker-actions">
              <div className="poker-plaque wide">Tap up to 3 cards to swap</div>
              <button type="button" className="poker-btn call" onClick={() => answer(picked)}>
                {picked.length ? `SWAP ${picked.length}` : "KEEP ALL"}
              </button>
            </div>
          )}

          {started && view && prompt?.type === "next" && (
            <button type="button" className="poker-btn call" onClick={() => answer(true)}>
              {prompt.last ? "FINISH" : "NEXT DEAL"}
            </button>
          )}

          {started && view && !prompt && (
            <div className="poker-plaque wide">
              {view.dealing ? "Dealing the cards..." : "Waiting for the other players..."}
            </div>
          )}
        </div>
      </div>

      {rulesOpen && (
        <div className="poker-modal" role="dialog" aria-modal="true" onClick={() => setRulesOpen(false)}>
          <div className="poker-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="poker-modal-title gold-text">RULES</div>
            <div className="poker-modal-body">
              {RULES.map((rule, i) => (
                <p key={i}>
                  {i + 1}. {rule}
                </p>
              ))}
            </div>
            <button type="button" className="poker-modal-close" onClick={() => setRulesOpen(false)}>
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
